import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Check, Minus, Star, Loader2 } from "lucide-react";

const PLANS = [
  { tier: "free", name: "Basic", blurb: "Everything you need to publish your first service." },
  { tier: "intermediate", name: "Intermediate", blurb: "Grow with analytics and more room to book.", popular: true },
  { tier: "pro", name: "Pro", blurb: "Unlimited everything, plus your own branding." },
];

// Price display per tier per billing interval.
const PRICES = {
  free: { month: { big: "$0", note: "forever" }, year: { big: "$0", note: "forever" } },
  intermediate: {
    month: { big: "$15", note: "/mo" },
    year: { big: "$150", note: "/yr", sub: "≈ $12.50/mo · 2 months free" },
  },
  pro: {
    month: { big: "$19", note: "/mo" },
    year: { big: "$190", note: "/yr", sub: "≈ $15.83/mo · 2 months free" },
  },
};

// Each row renders a cell per plan (order: free, intermediate, pro).
const FEATURE_ROWS = [
  { label: "Services allowed", cells: ["1", "10", "Unlimited"] },
  { label: "Bookings per month", cells: ["20", "300", "Unlimited"] },
  { label: "Analytics", cells: [false, true, true] },
  { label: "Custom branding", cells: [false, false, true] },
  { label: "Packages", cells: ["Invite-only (1)", "Create or join (1)", "Unlimited"] },
];

function Cell({ value }) {
  if (value === true)
    return <Check className="w-5 h-5 text-primary" strokeWidth={2.5} data-testid="cell-check" />;
  if (value === false)
    return <Minus className="w-5 h-5 text-muted-foreground/40" strokeWidth={2} data-testid="cell-dash" />;
  return <span className="text-sm font-medium">{value}</span>;
}

export default function Pricing() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);
  const [confirmDowngrade, setConfirmDowngrade] = useState(false);
  const [interval, setInterval] = useState("month");

  const isProvider = user && user.role === "subscriber";
  const isCustomer = user && user.role === "customer";
  const isAdmin = user && user.role === "admin";

  const startCheckout = async (tier) => {
    setLoadingTier(tier);
    try {
      const { data } = await api.post("/subscription/checkout", {
        tier,
        interval,
        origin_url: window.location.origin,
      });
      if (data.free) {
        navigate("/subscription/success?downgrade=1");
      } else if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoadingTier(null);
    }
  };

  const planButton = (plan) => {
    if (!user) {
      return (
        <Button
          className="w-full rounded-sm"
          variant={plan.popular ? "default" : "outline"}
          onClick={() => navigate(`/register?role=subscriber&next=/pricing`)}
          data-testid={`plan-signup-${plan.tier}`}
        >
          {plan.tier === "free" ? "Start for free" : "Get started"}
        </Button>
      );
    }
    if (isCustomer) {
      return (
        <Button variant="outline" className="w-full rounded-sm" onClick={() => navigate("/browse")}
          data-testid={`plan-browse-${plan.tier}`}>
          Browse services
        </Button>
      );
    }
    if (isAdmin) {
      return (
        <Button variant="secondary" className="w-full rounded-sm" disabled data-testid={`plan-admin-${plan.tier}`}>
          Provider plan
        </Button>
      );
    }
    // provider / subscriber
    const current = (user.tier || "free") === plan.tier;
    if (current) {
      return (
        <Button variant="secondary" className="w-full rounded-sm" disabled data-testid={`plan-current-${plan.tier}`}>
          Current plan
        </Button>
      );
    }
    if (plan.tier === "free") {
      return (
        <Button variant="outline" className="w-full rounded-sm" disabled={loadingTier !== null}
          onClick={() => setConfirmDowngrade(true)} data-testid="plan-select-free">
          {loadingTier === "free" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Switch to Basic"}
        </Button>
      );
    }
    return (
      <Button className="w-full rounded-sm" variant={plan.popular ? "default" : "outline"}
        onClick={() => startCheckout(plan.tier)} disabled={loadingTier !== null}
        data-testid={`plan-select-${plan.tier}`}>
        {loadingTier === plan.tier ? <Loader2 className="w-4 h-4 animate-spin" /> : `Choose ${plan.name}`}
      </Button>
    );
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="pricing-page">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Plans for providers</p>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter mt-3">Compare plans</h1>
        <p className="text-muted-foreground mt-3">Simple, transparent pricing. Upgrade or cancel anytime.</p>
      </div>

      {isCustomer && (
        <div className="mt-8 max-w-2xl mx-auto border border-border rounded-sm bg-muted/40 px-5 py-4 text-center text-sm text-muted-foreground"
          data-testid="pricing-customer-notice">
          You're signed in as a customer — plans are for service providers. Booking is always free for you.
        </div>
      )}

      {/* Billing interval toggle */}
      <div className="flex justify-center mt-10">
        <div className="inline-flex items-center gap-2 bg-white border border-border rounded-sm p-1 relative" data-testid="billing-toggle">
          {[
            { key: "month", label: "Monthly" },
            { key: "year", label: "Annual" },
          ].map((opt) => {
            const active = interval === opt.key;
            return (
              <button key={opt.key} onClick={() => setInterval(opt.key)}
                data-testid={`billing-${opt.key}`}
                className={`relative z-10 px-5 py-2 rounded-sm text-sm font-medium transition-colors ${
                  active ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}>
                {active && (
                  <motion.span layoutId="billing-pill" className="absolute inset-0 bg-primary rounded-sm -z-10"
                    transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                {opt.label}
              </button>
            );
          })}
          <span className="pr-3 pl-1 text-xs font-semibold text-primary whitespace-nowrap" data-testid="billing-save-hint">
            Save 2 months
          </span>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-5 mt-10 items-start">
        {PLANS.map((plan, pi) => {
          const p = PRICES[plan.tier][interval];
          return (
          <div key={plan.tier} data-testid={`plan-card-${plan.tier}`}
            className={`relative bg-white border rounded-sm flex flex-col ${
              plan.popular ? "border-primary border-2 hard-shadow md:-translate-y-3 z-10" : "border-border"
            }`}>
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-wider px-3 py-1 rounded-sm flex items-center gap-1"
                data-testid="most-popular-badge">
                <Star className="w-3 h-3 fill-current" /> Most popular
              </div>
            )}

            <div className="p-7 pb-5 border-b border-border">
              <h2 className="font-display text-2xl font-semibold">{plan.name}</h2>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display text-4xl font-semibold tracking-tighter" data-testid={`plan-price-${plan.tier}`}>{p.big}</span>
                <span className="text-muted-foreground text-sm">{p.note}</span>
              </div>
              <p className="text-xs text-primary font-medium mt-1 min-h-[16px]" data-testid={`plan-subprice-${plan.tier}`}>{p.sub || ""}</p>
              <p className="text-sm text-muted-foreground mt-3 leading-relaxed min-h-[40px]">{plan.blurb}</p>
            </div>

            <div className="p-7 pt-5 flex-1">
              <ul className="space-y-4">
                {FEATURE_ROWS.map((row) => (
                  <li key={row.label} className="flex items-center justify-between gap-3"
                    data-testid={`feature-${plan.tier}-${row.label.replace(/\s+/g, "-").toLowerCase()}`}>
                    <span className="text-sm text-muted-foreground">{row.label}</span>
                    <span className="flex justify-end items-center min-w-[90px] text-right">
                      <Cell value={row.cells[pi]} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-7 pt-0">
              {ready ? planButton(plan) : (
                <Button className="w-full rounded-sm" disabled><Loader2 className="w-4 h-4 animate-spin" /></Button>
              )}
            </div>
          </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-muted-foreground mt-10">
        {interval === "year"
          ? "Annual plans are billed once a year (2 months free) and can be canceled anytime from your dashboard."
          : "All paid plans are billed monthly and can be canceled anytime from your dashboard."}
      </p>

      <AlertDialog open={confirmDowngrade} onOpenChange={setConfirmDowngrade}>
        <AlertDialogContent data-testid="downgrade-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Switch to the Basic plan?</AlertDialogTitle>
            <AlertDialogDescription>
              Your paid subscription will be canceled and your account will move to the free Basic plan.
              You'll keep 1 service and 20 bookings a month, and lose analytics and other paid features.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="downgrade-cancel-btn">Keep my plan</AlertDialogCancel>
            <AlertDialogAction onClick={() => { setConfirmDowngrade(false); startCheckout("free"); }}
              data-testid="downgrade-confirm-btn">
              Switch to Basic
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
